/** @format */
import React, { useState, useEffect } from "react";
import moment from "moment";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart,
  Sector,
} from "recharts";
import {
  Users,
  UserCheck,
  UserX,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  Gift,
  LogOut,
} from "lucide-react";
import Cookies from "js-cookie";
import { motion } from "framer-motion";

// Custom Active Shape for Pie Chart with Emboss Effect
const renderActiveShape = (props) => {
  const RADIAN = Math.PI / 180;
  const {
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    startAngle,
    endAngle,
    fill,
    payload,
    percent,
    value,
  } = props;
  const sin = Math.sin(-RADIAN * midAngle);
  const cos = Math.cos(-RADIAN * midAngle);
  const sx = cx + (outerRadius + 10) * cos;
  const sy = cy + (outerRadius + 10) * sin;
  const mx = cx + (outerRadius + 30) * cos;
  const my = cy + (outerRadius + 30) * sin;
  const ex = mx + (cos >= 0 ? 1 : -1) * 22;
  const ey = my;
  const textAnchor = cos >= 0 ? "start" : "end";

  return (
    <g>
      {/* Shadow/Emboss effect */}
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius + 15}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
        opacity={0.3}
      />
      {/* Main sector with increased size */}
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius + 10}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
      {/* Connector line */}
      <path
        d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`}
        stroke={fill}
        fill="none"
        strokeWidth={2}
      />
      <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" />
      {/* Department name */}
      <text
        x={ex + (cos >= 0 ? 1 : -1) * 12}
        y={ey}
        textAnchor={textAnchor}
        fill="#334155"
        className="font-bold text-sm"
      >
        {payload.name}
      </text>
      {/* Employee count and percentage */}
      <text
        x={ex + (cos >= 0 ? 1 : -1) * 12}
        y={ey}
        dy={18}
        textAnchor={textAnchor}
        fill="#64748b"
        className="text-xs"
      >
        {`${value} employees (${(percent * 100).toFixed(1)}%)`}
      </text>
    </g>
  );
};

const Dashboard = () => {
  const API_URL = process.env.REACT_APP_FRONTEND_URL;
  const authToken = Cookies.get("user_token");

  const [data, setData] = useState({
    totalWorkforce: 0,
    presentWorkforce: 0,
    absentWorkforce: 0,
    lateArrivals: 0,
    inLeave: 0,
  });

  const [attendanceHistory, setAttendanceHistory] = useState([]);
  const [departmentData, setDepartmentData] = useState([]);
  const [pendingLeaves, setPendingLeaves] = useState([]);
  const [upcomingRetirements, setUpcomingRetirements] = useState([]);
  const [upcomingBirthdays, setUpcomingBirthdays] = useState([]);
  const [salaryData, setSalaryData] = useState({
    total: "0",
    percentageChange: "0%",
    monthlyTrend: [],
  });
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [activeTab, setActiveTab] = useState("leaves");
  const [activePieIndex, setActivePieIndex] = useState(0); // For pie chart hover
  const [loadingRetirements, setLoadingRetirements] = useState(false);

  const fetchDepartmentDistribution = async (orgId) => {
    if (!orgId) return;
    try {
      const res = await fetch(
        `${API_URL}/v1/hris/employees/employee-count?organization=${orgId}`,
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );

      const result = await res.json();

      if (result.success && result.data?.departments) {
        const formatted = result.data.departments.map((dept, index) => ({
          name: dept.name,
          value: dept.employeeCount,
          color: [
            "#3b82f6", // blue
            "#10b981", // green
            "#f59e0b", // yellow
            "#8b5cf6", // purple
            "#ec4899", // pink
            "#06b6d4", // cyan
            "#f97316", // orange
            "#84cc16", // lime
          ][index % 8],
        }));

        setDepartmentData(formatted);
      } else {
        console.warn("No department data found:", result);
        setDepartmentData([]);
      }
    } catch (error) {
      console.error("Error fetching department employee counts:", error);
    }
  };
  useEffect(() => {
    if (selectedOrg) {
      fetchDepartmentDistribution(selectedOrg);
    }
  }, [selectedOrg]);


  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const res = await fetch(`${API_URL}/v1/hris/organizations/organization`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        const result = await res.json();
        if (result.success && Array.isArray(result.data)) {
          setOrganizations(result.data);
          setSelectedOrg(result.data[0]?.id || null); // auto-select first org
        }
      } catch (error) {
        console.error("Error fetching organizations:", error);
      }
    };

    fetchOrganizations();
  }, [API_URL, authToken]);

  const fetchSalaryOverview = async () => {
    if (!selectedOrg) return; // wait until org selected
    try {
      const res = await fetch(
        `${API_URL}/v1/hris/payroll/getPayrollTotal?organization=${selectedOrg}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      if (res.status === 404) {
        // 🟠 handle not found case
        setSalaryData({
          total: "0",
          percentageChange: "0%",
          monthlyTrend: [],
        });
        console.warn("No payroll data found for this organization.");
        return;
      }

      const result = await res.json();

      if (result.success && Array.isArray(result.data)) {
        const formattedTrend = result.data.map((item) => ({
          month: moment(item.month, "YYYY-MM").format("MMM"),
          amount: parseFloat(item.total),
        }));

        const latest = result.data[result.data.length - 1];
        setSalaryData({
          total: latest?.total || "0",
          percentageChange: latest?.percentageChange || "0%",
          monthlyTrend: formattedTrend,
        });
      } else {
        // 🔴 handle "success:false"
        setSalaryData({
          total: "0",
          percentageChange: "0%",
          monthlyTrend: [],
        });
      }
    } catch (error) {
      console.error("Error fetching salary data:", error);
    }
  };
  useEffect(() => {
    fetchSalaryOverview();
  }, [API_URL, authToken, selectedOrg]);


  useEffect(() => {
    if (!selectedOrg) return; // 🟡 Wait until org is selected

    const fetchTodayStats = async () => {
      try {
        const today = moment().format("YYYY-MM-DD");
        const res = await fetch(
          `${API_URL}/v1/hris/new-attendence/all-attendance-counts?date=${today}&organization=${selectedOrg}`,
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
        const result = await res.json();

        if (result.success && result.summary) {
          setData({
            totalWorkforce: result.summary.totalActiveEmployees || 0,
            presentWorkforce: result.summary.presentWorkforce || 0,
            absentWorkforce: result.summary.absentWorkforce || 0,
            inLeave: result.summary.leaveCount || 0,
            lateArrivals: result.summary.lateArrivalsCount || 0,
          });
        } else {
          console.warn("Unexpected response structure:", result);
          setData({
            totalWorkforce: 0,
            presentWorkforce: 0,
            absentWorkforce: 0,
            inLeave: 0,
            lateArrivals: 0,
          });
        }


      } catch (error) {
        console.error("Error fetching attendance counts:", error);
      }
    };

    fetchTodayStats();
  }, [API_URL, authToken, selectedOrg]);

  useEffect(() => {
    if (!selectedOrg) return; // 🟡 Wait until org is selected

    const fetchAttendanceHistory = async () => {
      try {
        const res = await fetch(
          `${API_URL}/v1/hris/employees/getAttendanceStatsForLastFiveDays?organization=${selectedOrg}`,
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
        const result = await res.json();
        if (result.success && Array.isArray(result.data)) {
          const formatted = result.data.map((d) => ({
            day: moment(d.date).format("ddd"),
            present: d.presentWorkforce,
            absent: Math.abs(d.absentWorkforce), // 🟢 fix if negative
          }));
          setAttendanceHistory(formatted);
        }
      } catch (error) {
        console.error("Error fetching attendance history:", error);
      }
    };

    fetchAttendanceHistory();
  }, [API_URL, authToken, selectedOrg]);


  useEffect(() => {
    const fetchPendingLeaves = async () => {
      try {
        const res = await fetch(`${API_URL}/v1/hris/leave/getleaveapprove1`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        const result = await res.json();
        if (Array.isArray(result)) setPendingLeaves(result);
      } catch (error) {
        console.error("Error fetching pending leaves:", error);
      }
    };
    fetchPendingLeaves();
  }, [API_URL, authToken]);

  useEffect(() => {
    const fetchRetirements = async () => {
      if (!selectedOrg) return; // 🟡 Ensure organization is selected

      try {
        const url = `${API_URL}/v1/hris/employees/get-retirements?organization=${selectedOrg}`;

        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${authToken}` },
        });

        const result = await res.json();

        if (result.success && Array.isArray(result.data) && result.data.length > 0) {
          // ✅ Has retirement data
          setUpcomingRetirements(result.data);
        } else if (result.success && result.message) {
          // ✅ No data but message exists
          setUpcomingRetirements([{ noDataMessage: result.message }]);
        } else {
          // ✅ Handle fallback
          setUpcomingRetirements([
            { noDataMessage: "No employees reaching retirement age this year." },
          ]);
        }
      } catch (error) {
        console.error("Error fetching retirements:", error);
        setUpcomingRetirements([
          { noDataMessage: "Failed to load retirement data." },
        ]);
      }
    };

    fetchRetirements();
  }, [API_URL, authToken, selectedOrg]);


  useEffect(() => {
    if (!selectedOrg) return;

    const fetchBirthdays = async () => {
      try {
        const res = await fetch(
          `${API_URL}/v1/hris/employees/get-birthdays?organization=${selectedOrg}`,
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
        const result = await res.json();
        if (result.success && Array.isArray(result.data)) {
          setUpcomingBirthdays(result.data);
        }
      } catch (error) {
        console.error("Error fetching birthdays:", error);
      }
    };

    fetchBirthdays();
  }, [API_URL, authToken, selectedOrg]);



  const attendanceRate = data.totalWorkforce
    ? ((data.presentWorkforce / data.totalWorkforce) * 100).toFixed(1)
    : 0;

  const onPieEnter = (_, index) => {
    setActivePieIndex(index);
  };

  return (

    <motion.div
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      transition={{ duration: 0.4 }}
    >



      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-3">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
            HR Dashboard
          </h1>
          <p className="text-slate-600 mt-1">
            Employee Information Management System
          </p>
        </div>

        <div className="mb-4 flex items-center gap-3">
          <label className="text-sm font-medium text-slate-600">Organization:</label>
          <select
            value={selectedOrg || ""}
            onChange={(e) => setSelectedOrg(e.target.value)}
            className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select Organization</option>
            {organizations.map((org) => (
              <option key={org.id} value={org.id}>
                {org.organization_name}
              </option>
            ))}
          </select>
        </div>

        {/* Top Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          {/* Total Workforce */}
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200 hover:shadow-xl transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 text-sm font-medium">Total Workforce</p>
                <p className="text-3xl font-bold text-slate-800 mt-2">
                  {data.totalWorkforce}
                </p>
                <p className="text-xs text-slate-500 mt-2">Active employees</p>
              </div>
              <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-4 rounded-xl">
                <Users className="text-white" size={28} />
              </div>
            </div>
          </div>

          {/* Present */}
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200 hover:shadow-xl transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 text-sm font-medium">Present Today</p>
                <p className="text-3xl font-bold text-green-600 mt-2">
                  {data.presentWorkforce}
                </p>
                <p className="text-xs text-green-600 mt-2 font-semibold">
                  {attendanceRate}% attendance
                </p>
              </div>
              <div className="bg-gradient-to-br from-green-500 to-emerald-600 p-4 rounded-xl">
                <UserCheck className="text-white" size={28} />
              </div>
            </div>
          </div>

          {/* Absent */}
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200 hover:shadow-xl transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 text-sm font-medium">Absent Today</p>
                <p className="text-3xl font-bold text-red-600 mt-2">
                  {data.absentWorkforce}
                </p>
                <p className="text-xs text-slate-500 mt-2">
                  Including {data.inLeave} on leave
                </p>
              </div>
              <div className="bg-gradient-to-br from-red-500 to-rose-600 p-4 rounded-xl">
                <UserX className="text-white" size={28} />
              </div>
            </div>
          </div>

          {/* Late arrivals */}
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200 hover:shadow-xl transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 text-sm font-medium">Late Arrivals</p>
                <p className="text-3xl font-bold text-orange-600 mt-2">
                  {data.lateArrivals}
                </p>
                <p className="text-xs text-slate-500 mt-2">Today's count</p>
              </div>
              <div className="bg-gradient-to-br from-orange-500 to-amber-600 p-4 rounded-xl">
                <AlertCircle className="text-white" size={28} />
              </div>
            </div>
          </div>
        </div>

        {/* Salary + Department */}
        <div className="grid grid-cols-3 gap-6 mb-6">
          {/* Monthly Salary Overview */}
          <div className="col-span-2 bg-white rounded-2xl p-6 shadow-lg border border-slate-200">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-slate-800">Monthly Salary Overview</h2>
                <p className="text-slate-600 text-sm">Total payroll expenses</p>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-slate-800">
                  LKR{" "}
                  {parseFloat(salaryData.total).toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </p>
                <div
                  className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-semibold mt-2 ${parseFloat(salaryData.percentageChange) >= 0
                    ? "bg-green-100 text-green-700"
                    : "bg-red-100 text-red-700"
                    }`}
                >
                  {parseFloat(salaryData.percentageChange) >= 0 ? (
                    <TrendingUp size={16} />
                  ) : (
                    <TrendingDown size={16} />
                  )}
                  {salaryData.percentageChange}
                </div>
              </div>
            </div>

            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={salaryData.monthlyTrend}>
                <defs>
                  <linearGradient id="colorAmount" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" stroke="#64748b" />
                <YAxis
                  stroke="#64748b"
                  tickFormatter={(value) => `${(value / 1000000).toFixed(1)}M`}
                />
                <Tooltip
                  formatter={(value) => `LKR ${value.toLocaleString()}`}
                  contentStyle={{
                    borderRadius: "12px",
                    border: "1px solid #e2e8f0",
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="amount"
                  stroke="#3b82f6"
                  strokeWidth={3}
                  fillOpacity={1}
                  fill="url(#colorAmount)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Department Distribution with Enhanced Pie Chart */}
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200">
            <h2 className="text-xl font-bold text-slate-800 mb-4">
              Department Distribution
            </h2>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  activeIndex={activePieIndex}
                  activeShape={renderActiveShape}
                  data={departmentData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="value"
                  onMouseEnter={onPieEnter}
                  animationBegin={0}
                  animationDuration={800}
                  animationEasing="ease-out"
                >
                  {departmentData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.color}
                      style={{
                        filter:
                          index === activePieIndex
                            ? "drop-shadow(0px 0px 8px rgba(0,0,0,0.3))"
                            : "none",
                        cursor: "pointer",
                      }}
                    />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value, name) => [`${value} Employees`, name]}
                  contentStyle={{
                    borderRadius: "12px",
                    border: "1px solid #e2e8f0",
                  }}
                />
              </PieChart>
            </ResponsiveContainer>



          </div>
        </div>

        {/* Attendance + Notifications */}
        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-2xl p-6 shadow-lg border border-slate-200">
            <h2 className="text-xl font-bold text-slate-800 mb-4">
              Weekly Attendance Trends
            </h2>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={attendanceHistory}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="day" stroke="#64748b" />
                <YAxis stroke="#64748b" />
                <Tooltip
                  contentStyle={{
                    borderRadius: "12px",
                    border: "1px solid #e2e8f0",
                  }}
                />
                <Legend />
                <Bar dataKey="present" fill="#10b981" radius={[8, 8, 0, 0]} />
                <Bar dataKey="absent" fill="#ef4444" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Notifications */}
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200">
            <div className="flex border-b border-slate-200 mb-4">
              <button
                className={`px-4 py-2 text-sm font-semibold transition-all ${activeTab === "leaves"
                  ? "border-b-2 border-blue-500 text-blue-600"
                  : "text-slate-600 hover:text-slate-800"
                  }`}
                onClick={() => setActiveTab("leaves")}
              >
                Leaves ({pendingLeaves.length})
              </button>
              <button
                className={`px-4 py-2 text-sm font-semibold transition-all ${activeTab === "retirements"
                  ? "border-b-2 border-blue-500 text-blue-600"
                  : "text-slate-600 hover:text-slate-800"
                  }`}
                onClick={() => setActiveTab("retirements")}
              >
                <LogOut size={14} className="inline mr-1" />
                Retirements
              </button>
              <button
                className={`px-4 py-2 text-sm font-semibold transition-all ${activeTab === "birthdays"
                  ? "border-b-2 border-blue-500 text-blue-600"
                  : "text-slate-600 hover:text-slate-800"
                  }`}
                onClick={() => setActiveTab("birthdays")}
              >
                <Gift size={14} className="inline mr-1" />
                Birthdays
              </button>
            </div>

            <div className="space-y-3 max-h-72 overflow-y-auto">
              {activeTab === "leaves" &&
                pendingLeaves.slice(0, 4).map((leave, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition-all"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                        {leave.employee_fullname
                          ?.split(" ")
                          .map((n) => n[0])
                          .join("")}
                      </div>
                      <div>
                        <p className="font-semibold text-slate-800 text-sm">
                          {leave.employee_fullname}
                        </p>
                        <p className="text-xs text-slate-500">
                          {leave.employee_no} • {leave.days}
                        </p>
                      </div>
                    </div>
                    <AlertCircle className="text-orange-500" size={18} />
                  </div>
                ))}

              {activeTab === "retirements" && (
                <>
                  {loadingRetirements ? (
                    <p className="text-center text-slate-500 text-sm py-3">
                      Loading retirements...
                    </p>
                  ) : upcomingRetirements.length === 0 ? (
                    <p className="text-center text-slate-500 text-sm py-3 italic">
                      No employees reaching retirement age this year.
                    </p>
                  ) : upcomingRetirements[0]?.noDataMessage ? (
                    <p className="text-center text-slate-500 text-sm py-3 italic">
                      {upcomingRetirements[0].noDataMessage}
                    </p>
                  ) : (
                    upcomingRetirements.map((r, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition-all"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                            {r.name?.split(" ").map((n) => n[0]).join("")}
                          </div>
                          <div>
                            <p className="font-semibold text-slate-800 text-sm">{r.name}</p>
                            <p className="text-xs text-slate-500">{r.retirementDate}</p>
                          </div>
                        </div>
                        <span className="text-xs text-slate-600 italic">{r.message}</span>
                      </div>
                    ))
                  )}
                </>
              )}



              {activeTab === "birthdays" &&
                upcomingBirthdays.map((b, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition-all"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-rose-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                        {b.name?.split(" ").map((n) => n[0]).join("")}
                      </div>
                      <div>
                        <p className="font-semibold text-slate-800 text-sm">
                          {b.name}
                        </p>
                        <p className="text-xs text-slate-500">{b.birthday}</p>
                      </div>
                    </div>
                    <span className="text-xs font-semibold text-pink-600">
                      {b.label}
                    </span>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default Dashboard;